import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Fiat 500 (Nuova 500, classic ~1965) — a tiny rear-engine bubble car at true
// scale (1 unit = 1 metre): length ~2.97 (the shortest car in the set), width
// 1.32, height 1.32 (as tall as it is wide — a tall narrow bubble), wheelbase
// 1.84 on tiny 12-inch steel wheels with chrome hubcaps. Nose is +Z. Like the
// Beetle, the whole shell is composed from rotation-free scaled spheres so it
// reads as one rounded egg, never as slab boxes: a wide-based cabin dome over a
// narrow lower body, a short bonnet dipping to a low rounded nose between the
// faired-in headlamp wings, and a short rounded engine-lid tail. Upright small
// windscreen and a large glass area for such a small car, a body-colour
// B-pillar, a dark full-length canvas-roof hint sunk into the dome, thin chrome
// blade bumpers with overriders, round headlamps, tiny round tail lamps, engine
// louvres and a single round mirror. Body panels carry the "paint" recolour
// slot (classic Fiat pastel cream); `drive` spins the wheels and `hazards`
// blinks the four indicators.

const PAINT = "#e8d9a0"; // classic Fiat pastel cream — the "paint" slot default
const TRIM = "#17181b"; // running boards, underbody, valances, louvres
const WELL = "#141518"; // wheel-well discs
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6"; // fine bright trim
const BUMPER = "#c9ced2"; // light silver — bumpers + hubcaps, reads against the cream body
const SILVER = "#adb3ba"; // steel wheel discs
const TIRE = "#1a1b1d";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";
const CANVAS = "#33302a"; // rolled-back fabric roof hint

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.2; // tiny wheels
const TYRE_W = 0.13; // narrow tyres
const WHEEL_X = 0.55; // half track — tyre face proud of the narrow body
const AXLE_F = 0.85;
const AXLE_R = -0.99; // wheelbase 1.84

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). */
function blob(scale: Vec3, position: Vec3): Part {
  return {
    geometry: new THREE.SphereGeometry(1, 16, 12),
    color: PAINT,
    position,
    scale,
    slot: "paint",
  };
}

/** One tiny steel wheel: narrow tyre, silver steel disc slightly recessed, a
 *  chrome hubcap and a small chrome dome cap flush with the tyre face. Every
 *  part shares `name` so the `drive` track spins them as one node. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  return [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 16, SILVER, [x + side * 0.04, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.095, 0.095, 0.03, 16, BUMPER, [x + side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    {
      geometry: new THREE.SphereGeometry(0.05, 12, 8),
      color: BUMPER,
      position: [x + side * 0.065, y, z] as Vec3,
      scale: [0.6, 1, 1] as Vec3,
      name,
    },
  ];
}

/** The wheel arch: a dark well disc proud of the narrow body side (behind the
 *  prouder tyre face) plus a thin dark brow — an upper half-torus over the
 *  tyre. The [0, pi/2, 0] yaw puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.23, 0.23, 0.02, 18, WELL, [side * 0.47, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.225, 0.028, 6, 18, Math.PI),
      color: TRIM,
      position: [side * 0.49, WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.46, 0.56, 1.08] },
  { name: "indicator-fr", pos: [0.46, 0.56, 1.08] },
  { name: "indicator-rl", pos: [-0.4, 0.52, -1.34] },
  { name: "indicator-rr", pos: [0.4, 0.52, -1.34] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.035, 0.035, 1.02, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(0.86, 0.14, 2.1, TRIM, [0, 0.2, -0.05])); // underbody pan, tucked inside the body

  // -- Rounded body masses (paint): the whole shell is blobs, no slab sides ----
  P.push(blob([0.58, 0.42, 1.06], [0, 0.46, -0.04])); // lower body — widest at the doors, tucking in at both axles
  P.push(blob([0.56, 0.48, 0.74], [0, 0.78, -0.08])); // cabin dome — wide-based egg, top near full height
  P.push(blob([0.48, 0.26, 0.34], [0, 0.5, 0.92])); // short low bonnet sloping toward the nose
  P.push(blob([0.44, 0.26, 0.18], [0, 0.46, 1.1])); // low rounded nose cap (short front overhang)
  P.push(blob([0.54, 0.42, 0.42], [0, 0.5, -1.0])); // engine-lid tail — one rounded arc to the rear
  P.push(paint(0.84, 0.3, 0.32, [0, 0.34, 1.14])); // front valance filling under the nose to the bumper
  P.push(paint(0.88, 0.32, 0.34, [0, 0.36, -1.24])); // rear valance filling under the tail to the bumper

  // -- Sills: low thin dark rocker strips joining the arches -------------------
  for (const s of S) P.push(box(0.05, 0.06, 1.4, TRIM, [s * 0.49, 0.26, -0.05]));

  // -- Greenhouse: flat panes seated just proud of the dome, each backed by paint
  // Backer panels sit behind each pane so no daylight shows behind it; the side
  // glass is a straight band just proud of the dome, its painted pillars
  // (including the body-colour B-pillar) proud of that again.
  P.push(paint(0.58, 0.34, 0.07, [0, 1.0, 0.5])); // windscreen backer / surround
  P.push(box(0.56, 0.3, 0.05, SCREEN, [0, 1.0, 0.54], [-0.35, 0, 0])); // upright windscreen (large 500 glass)
  P.push(paint(0.5, 0.26, 0.07, [0, 0.98, -0.67])); // rear window backer / surround
  P.push(box(0.46, 0.22, 0.04, GLASS, [0, 0.98, -0.71], [0.4, 0, 0])); // small rear window, tilted on the rear face
  for (const s of S) {
    P.push(paint(0.08, 0.28, 0.07, [s * 0.34, 1.02, 0.57], [-0.35, 0, 0])); // A-pillar post
    P.push(box(0.05, 0.18, 0.9, GLASS, [s * 0.53, 0.89, -0.09], [0, 0, s * 0.04])); // small side glass at the shoulder
    P.push(paint(0.07, 0.2, 0.09, [s * 0.535, 0.89, 0.35])); // front pillar cap
    P.push(paint(0.09, 0.2, 0.09, [s * 0.54, 0.89, -0.08])); // body-colour B-pillar
    P.push(paint(0.07, 0.2, 0.1, [s * 0.535, 0.89, -0.51])); // rear quarter pillar cap
    P.push(box(0.02, 0.02, 0.86, CHROME, [s * 0.545, 0.79, -0.09])); // thin chrome belt-line trim
  }
  P.push(box(0.36, 0.05, 0.52, CANVAS, [0, 1.26, -0.03])); // canvas-roof hint sunk into the dome crown

  // -- Front fascia ------------------------------------------------------------
  for (const s of S) {
    // Round headlamp faired into the front wing: a chrome rim ring with a glass
    // eye proud of it, facing +Z.
    P.push(cyl(0.1, 0.1, 0.05, 16, CHROME, [s * 0.42, 0.66, 1.06], [Math.PI / 2, 0, 0]));
    P.push(cyl(0.075, 0.075, 0.03, 16, LAMP, [s * 0.42, 0.66, 1.09], [Math.PI / 2, 0, 0]));
    P.push(box(0.05, 0.14, 0.06, CHROME, [s * 0.26, 0.4, 1.33])); // overrider post on the blade
  }
  P.push(cyl(0.05, 0.05, 0.02, 16, CHROME, [0, 0.58, 1.29], [Math.PI / 2, 0, 0])); // round nose badge
  P.push(box(0.3, 0.03, 0.02, CHROME, [0, 0.46, 1.3])); // chrome nose trim strip
  P.push(box(1.0, 0.05, 0.05, CHROME, [0, 0.36, 1.33])); // thin chrome blade bumper hugging the nose
  P.push(box(0.06, 0.08, 0.04, TRIM, [0, 0.2, 1.3])); // plate bracket low on the nose
  P.push(box(0.26, 0.1, 0.02, PLATE, [0, 0.2, 1.31])); // front plate below the bumper

  // -- Rear fascia -------------------------------------------------------------
  // Engine cooling louvres: each slat's z follows the convex tail so it stays
  // proud instead of being swallowed (a flat stack sinks into the curve).
  for (const y of [0.76, 0.7, 0.64, 0.58]) {
    const z = -1.03 - 0.46 * Math.sqrt(1 - ((y - 0.5) / 0.4) ** 2) - 0.012;
    P.push(box(0.34, 0.014, 0.02, TRIM, [0, y, z]));
  }
  P.push(box(0.12, 0.03, 0.04, CHROME, [0, 0.8, -1.35])); // engine-lid handle high on the lid
  for (const s of S) {
    P.push(cyl(0.05, 0.05, 0.03, 14, CHROME, [s * 0.42, 0.62, -1.3], [Math.PI / 2, 0, 0])); // tail-lamp base
    P.push(cyl(0.038, 0.038, 0.05, 14, RED, [s * 0.42, 0.62, -1.33], [Math.PI / 2, 0, 0])); // tiny round tail lamp
    P.push(box(0.05, 0.14, 0.06, CHROME, [s * 0.26, 0.4, -1.47])); // overrider post on the blade
  }
  P.push(box(1.0, 0.05, 0.05, CHROME, [0, 0.36, -1.47])); // thin chrome blade bumper hugging the tail
  P.push(box(0.06, 0.08, 0.04, TRIM, [0, 0.2, -1.44])); // plate bracket low on the tail
  P.push(box(0.26, 0.1, 0.02, PLATE, [0, 0.2, -1.45])); // rear plate below the bumper

  // -- Furniture ---------------------------------------------------------------
  // A single round mirror on the driver's-side front wing.
  P.push(box(0.14, 0.015, 0.015, TRIM, [-0.45, 0.82, 0.45])); // mirror stalk reaching out from the wing
  P.push(cyl(0.045, 0.045, 0.02, 12, CHROME, [-0.53, 0.83, 0.45], [0, 0, Math.PI / 2])); // round mirror head
  for (const s of S) {
    P.push(box(0.02, 0.02, 0.09, CHROME, [s * 0.53, 0.64, 0.1])); // door handle
    P.push(box(0.14, 0.012, 0.018, TRIM, [s * 0.12, 0.9, 0.73], [-0.35, 0, 0])); // wiper at the windscreen base
  }
  for (const ind of INDICATORS) {
    P.push(box(0.05, 0.04, 0.04, AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const fiat500Classic = defineModel({
  id: "fiat-500-classic",
  name: "Fiat 500 (Classic)",
  batchId: "cars",
  tags: ["car", "fiat", "500", "classic", "citycar", "compact"],
  summary: "A tiny 1960s Fiat 500 rear-engine bubble car at true scale, in pastel cream.",
  description:
    "A classic Nuova Fiat 500 at real 1 unit = 1 metre scale (about 2.97 long " +
    "— the shortest car in the set — 1.32 wide, 1.32 tall, 1.84 wheelbase, on " +
    "tiny steel wheels with chrome hubcaps). The rounded bubble shell is built " +
    "from scaled spheres: a wide-based cabin dome over a narrow lower body, a " +
    "short bonnet dipping to a low rounded nose between the faired-in headlamp " +
    "wings, and a short rounded rear-engine tail. Upright small windscreen with " +
    "a large glass area, a body-colour B-pillar, recessed side glass with thin " +
    "chrome trim, a dark full-length canvas-roof hint sunk into the dome, a " +
    "small tilted rear window over engine cooling louvres. Thin chrome blade " +
    "bumpers with overriders front and rear, round headlamps, a nose badge and " +
    "chrome nose trim, tiny round tail lamps, a single round mirror, door " +
    "handles and plates. Body panels carry the 'paint' recolour slot (pastel " +
    "cream); `drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 1.65,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
